/**
 * `useAskUserRehydrate` — rebuild an ask-user card ON MOUNT from a mission that
 * is still parked, so a reload does not lose the lawyer's only way to answer.
 *
 * Fixes `ui-tester` I-…-4 (run `ui-20260906-0033-localdev`): the card mounts
 * from the `lx_ask_user` tool broadcast and lives in component state, and an
 * EventStream frame is not replayed — so a reload left nothing on screen while
 * the mission stayed parked, holding the case's only admission slot, and the
 * assistant kept insisting the questions were already there.
 *
 * ## What it does NOT do
 *
 * It does not poll. A park is not a live thing to watch — it is a state the
 * lawyer walked back into, and the WS path owns everything that happens after
 * mount. One read per (case, mount) is the whole mechanism, which also means a
 * mission-service that is down costs a silent no-op rather than a retry storm.
 *
 * It does not decide the SURFACE either — `rehydrateAskFromMission` does, via
 * `answerable`. See that module's header for why a non-answerable park must
 * never take over the composer.
 */

import { useEffect, useRef, useState } from "react";
import { getMissionAwaiting, listMissions } from "@/api/lx-mission";
import {
  abandonAttempt,
  beginAttempt,
  settleAttempt,
  type AttemptRecord,
} from "@/lib/attempt-gate";
import { rehydrateAskFromMission, type RehydratedAsk } from "@/lib/ask-user-rehydrate";
import { clientLog } from "@/lib/client-log";
import { parseLxSessionKey } from "@/types/session";

const log = clientLog.forSubsystem("chat");

export function useAskUserRehydrate(
  /**
   * The session this composer speaks for. The case is DERIVED from it rather
   * than passed separately, and that is a correctness choice, not tidiness.
   *
   * 🚨 Two sources would let them disagree. `composerSessionKey` and the active
   * selection are updated by different stores, so mid-navigation the composer
   * can speak for case B while the selection still reads case A — and this hook
   * would then fetch A's parked questions and stamp them with B's sessionKey,
   * rendering one case's interview in another case's composer. That is the
   * exact misdelivery `askBelongsToSession` exists to prevent on the live path
   * (measured in the browser; see `AskUserCardData.sessionKey`). Deriving the
   * case from the key makes the disagreement unrepresentable.
   */
  sessionKey: string | undefined,
): RehydratedAsk | null {
  const [ask, setAsk] = useState<RehydratedAsk | null>(null);
  // One attempt per case. A second run would re-raise a card the lawyer has
  // already dismissed, which is its own small betrayal.
  //
  // 🚨 An ABANDONED attempt does not count as the attempt — that distinction is
  // the whole of `ec7ba502c`'s bug. A bare `useRef<string>` latch set before the
  // first await cannot express it, and under StrictMode it consumed the one
  // attempt with a read whose result was then discarded. See `lib/attempt-gate`.
  const attempt = useRef<AttemptRecord>(null);

  useEffect(() => {
    if (!sessionKey) return;
    const ctx = parseLxSessionKey(sessionKey);
    const caseId = ctx?.type === "case" ? ctx.caseId : undefined;
    // Fail-closed: no resolvable CASE scope ⇒ nothing rehydrates. A park
    // belongs to a case, and an unscoped guess is how an ask lands in the
    // wrong thread.
    if (!caseId) return;
    const token = Symbol("ask-rehydrate");
    const claim = beginAttempt(attempt.current, caseId, token);
    if (!claim.proceed) return;
    attempt.current = claim.next;
    let cancelled = false;

    (async () => {
      try {
        const { missions } = await listMissions({ state: "awaiting_user", caseId });
        if (cancelled || !missions?.length) return;
        for (const m of missions) {
          try {
            const view = await getMissionAwaiting(m.missionId);
            if (cancelled) return;
            const rebuilt = rehydrateAskFromMission(view, {
              missionId: m.missionId,
              sessionKey,
            });
            if (!rebuilt) continue;
            log.info("chat.ask_user.rehydrated_from_mission", {
              session_key: sessionKey,
              mission_id: m.missionId,
              park_kind: rebuilt.parkKind,
              answerable: rebuilt.answerable,
              questions: rebuilt.card.questions.length,
              // ⚖ R1 — without this, the log cannot tell the WIZARD apart from
              // the read-only notice for an answerable park: `answerable:true`
              // reads as "the wizard mounted" and over the cap it did not.
              over_ask_cap: rebuilt.overAskCap,
              // ⚖ R6 — the same argument one field down. The notice now has
              // THREE arms, and `answerable:false` + `over_ask_cap:false` maps
              // to TWO different sentences: a park the LAWYER owes (he can
              // reply in chat) and one an AGENT owes (he cannot help). Without
              // this the log cannot say which one he was shown.
              human_owed: rebuilt.humanOwed,
            });
            setAsk(rebuilt);
            // One card at a time — the guide's rule, and it applies to a
            // rebuilt card exactly as to a live one.
            return;
          } catch {
            // 409 not_awaiting is the EXPECTED race: the park resolved between
            // the list and this read. Nothing to surface — the absence of a
            // card is the correct outcome, not an error the lawyer can act on.
          }
        }
      } catch {
        // Mission-service unreachable ⇒ no card. Deliberately silent to the
        // lawyer: they still have the assistant, and a banner about a service
        // they cannot reach would be noise about a thing they cannot fix.
      } finally {
        // The read produced its outcome — card, empty, or error alike — so the
        // case is answered and must not be re-asked on every re-render. A
        // no-op if this pass was already abandoned and replaced.
        attempt.current = settleAttempt(attempt.current, token);
      }
    })();

    return () => {
      cancelled = true;
      // Torn down before the read settled ⇒ this pass never happened, so it
      // must hand the case back rather than consume its one attempt. Settled
      // attempts are kept, which is what stops a dismissed card re-raising.
      attempt.current = abandonAttempt(attempt.current, token);
    };
  }, [sessionKey]);

  return ask;
}
